/**
 * Plausibilitätsprüfung für Forderungsakten (Mietwagen-Haftpflichtfall).
 *
 * Liefert 4 Ampel-Stati plus zugehörige Erklärungstexte für das Akten-Detail
 * im Forderungsmanager. Die Schwellwerte (14/30/90/180 Tage) stammen aus
 * Issue #267 — sie werden hier als Konstanten gehalten; die Konfigurierbarkeit
 * via Settings ist Teil eines separaten Sub-Issues.
 *
 * Die Funktion arbeitet rein auf den vorhandenen Feldern einer Forderungsakte
 * und funktioniert damit auch, bevor die Supabase-RPC aus #267 live ist.
 */

export type PruefStatus = 'gruen' | 'gelb' | 'rot' | 'unbekannt';

export interface PruefErgebnis {
  status: PruefStatus;
  wert: string;
  erklaerung: string;
}

export interface PlausibilitaetsInput {
  unfall_datum: string | null;
  mietbeginn: string | null;
  mietende: string | null;
  mietdauer_tage: number | null;
  reparaturdauer_voraussichtlich: number | null;
  reparaturdauer_tatsaechlich: number | null;
  rechnung_datum: string | null;
  verjährungsfrist: string | null;
}

export interface PlausibilitaetsErgebnis {
  schadentag: PruefErgebnis;
  abrechnung: PruefErgebnis;
  mietdauer: PruefErgebnis;
  verjaehrung: PruefErgebnis;
}

export const SCHWELLWERTE = {
  SCHADENTAG_GRUEN_TAGE: 14,
  SCHADENTAG_GELB_TAGE: 30,
  ABRECHNUNG_GRUEN_TAGE: 30,
  ABRECHNUNG_GELB_TAGE: 90,
  MIETDAUER_TOLERANZ_TAGE: 2,
  MIETDAUER_GELB_OBERGRENZE_TAGE: 7,
  VERJAEHRUNG_ROT_TAGE: 90,
  VERJAEHRUNG_GELB_TAGE: 180,
} as const;

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function diffInTagen(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

export function pruefeSchadentag(input: PlausibilitaetsInput): PruefErgebnis {
  const schaden = toDate(input.unfall_datum);
  const miet = toDate(input.mietbeginn);
  if (!schaden || !miet) {
    return {
      status: 'unbekannt',
      wert: '—',
      erklaerung: 'Schaden- oder Mietbeginn-Datum fehlt; Abstand kann nicht geprüft werden.',
    };
  }
  const diff = Math.abs(diffInTagen(miet, schaden));
  const { SCHADENTAG_GRUEN_TAGE, SCHADENTAG_GELB_TAGE } = SCHWELLWERTE;
  if (diff <= SCHADENTAG_GRUEN_TAGE) {
    return {
      status: 'gruen',
      wert: `${diff} Tage`,
      erklaerung: `Mietbeginn liegt ${diff} Tage vom Schadendatum entfernt — innerhalb des ${SCHADENTAG_GRUEN_TAGE}-Tage-Schwellwerts (grün).`,
    };
  }
  if (diff <= SCHADENTAG_GELB_TAGE) {
    return {
      status: 'gelb',
      wert: `${diff} Tage`,
      erklaerung: `Mietbeginn liegt ${diff} Tage nach Schadendatum — oberhalb des ${SCHADENTAG_GRUEN_TAGE}-Tage-Schwellwerts, unterhalb ${SCHADENTAG_GELB_TAGE} Tage (gelb).`,
    };
  }
  return {
    status: 'rot',
    wert: `${diff} Tage`,
    erklaerung: `Mietbeginn liegt ${diff} Tage nach Schadendatum — oberhalb des ${SCHADENTAG_GELB_TAGE}-Tage-Schwellwerts (rot). Zusammenhang zum Unfall ist plausibilitätskritisch.`,
  };
}

export function pruefeAbrechnung(input: PlausibilitaetsInput): PruefErgebnis {
  const mietende = toDate(input.mietende);
  const rechnung = toDate(input.rechnung_datum);
  if (!mietende || !rechnung) {
    return {
      status: 'unbekannt',
      wert: '—',
      erklaerung: 'Mietende oder Rechnungsdatum fehlt; Abrechnungs-Delay kann nicht geprüft werden.',
    };
  }
  const diff = diffInTagen(rechnung, mietende);
  const { ABRECHNUNG_GRUEN_TAGE, ABRECHNUNG_GELB_TAGE } = SCHWELLWERTE;
  if (diff < 0) {
    return {
      status: 'rot',
      wert: `${diff} Tage`,
      erklaerung: `Rechnungsdatum liegt ${Math.abs(diff)} Tage vor Mietende — unplausibel (rot). Mietende oder Rechnungsdatum prüfen.`,
    };
  }
  if (diff <= ABRECHNUNG_GRUEN_TAGE) {
    return {
      status: 'gruen',
      wert: `${diff} Tage`,
      erklaerung: `Rechnung wurde ${diff} Tage nach Mietende erstellt — innerhalb des ${ABRECHNUNG_GRUEN_TAGE}-Tage-Schwellwerts (grün).`,
    };
  }
  if (diff <= ABRECHNUNG_GELB_TAGE) {
    return {
      status: 'gelb',
      wert: `${diff} Tage`,
      erklaerung: `Rechnung wurde ${diff} Tage nach Mietende erstellt — zwischen ${ABRECHNUNG_GRUEN_TAGE} und ${ABRECHNUNG_GELB_TAGE} Tagen (gelb).`,
    };
  }
  return {
    status: 'rot',
    wert: `${diff} Tage`,
    erklaerung: `Rechnung wurde ${diff} Tage nach Mietende erstellt — oberhalb ${ABRECHNUNG_GELB_TAGE} Tagen (rot). Versicherung könnte verspätete Abrechnung monieren.`,
  };
}

export function pruefeMietdauer(input: PlausibilitaetsInput): PruefErgebnis {
  const mietdauer = input.mietdauer_tage;
  const reparatur =
    input.reparaturdauer_tatsaechlich ?? input.reparaturdauer_voraussichtlich ?? null;
  if (mietdauer == null || reparatur == null) {
    return {
      status: 'unbekannt',
      wert: '—',
      erklaerung: 'Mietdauer oder Reparaturdauer fehlt; Verhältnis kann nicht geprüft werden.',
    };
  }
  const diff = mietdauer - reparatur;
  const { MIETDAUER_TOLERANZ_TAGE, MIETDAUER_GELB_OBERGRENZE_TAGE } = SCHWELLWERTE;
  if (diff <= MIETDAUER_TOLERANZ_TAGE) {
    return {
      status: 'gruen',
      wert: `${mietdauer} / ${reparatur} Tage`,
      erklaerung: `Mietdauer (${mietdauer} Tage) übersteigt die Reparaturdauer (${reparatur} Tage) um maximal ${MIETDAUER_TOLERANZ_TAGE} Tage — akzeptabel (grün).`,
    };
  }
  if (diff <= MIETDAUER_GELB_OBERGRENZE_TAGE) {
    return {
      status: 'gelb',
      wert: `${mietdauer} / ${reparatur} Tage`,
      erklaerung: `Mietdauer (${mietdauer} Tage) übersteigt die Reparaturdauer (${reparatur} Tage) um ${diff} Tage — prüfungsbedürftig (gelb).`,
    };
  }
  return {
    status: 'rot',
    wert: `${mietdauer} / ${reparatur} Tage`,
    erklaerung: `Mietdauer (${mietdauer} Tage) übersteigt die Reparaturdauer (${reparatur} Tage) um ${diff} Tage — deutlich zu lang (rot). Eigenersparnis/Kürzung wahrscheinlich.`,
  };
}

export function pruefeVerjaehrung(
  input: PlausibilitaetsInput,
  heute: Date = new Date(),
): PruefErgebnis {
  const verj = toDate(input.verjährungsfrist);
  if (!verj) {
    return {
      status: 'unbekannt',
      wert: '—',
      erklaerung: 'Verjährungsfrist ist nicht gesetzt; Restlaufzeit kann nicht geprüft werden.',
    };
  }
  const diff = diffInTagen(verj, heute);
  const { VERJAEHRUNG_ROT_TAGE, VERJAEHRUNG_GELB_TAGE } = SCHWELLWERTE;
  if (diff < VERJAEHRUNG_ROT_TAGE) {
    return {
      status: 'rot',
      wert: `${diff} Tage`,
      erklaerung: `Verjährung in ${diff} Tagen — unterhalb des ${VERJAEHRUNG_ROT_TAGE}-Tage-Schwellwerts (rot). Dringend Klage bzw. Mahnbescheid prüfen.`,
    };
  }
  if (diff < VERJAEHRUNG_GELB_TAGE) {
    return {
      status: 'gelb',
      wert: `${diff} Tage`,
      erklaerung: `Verjährung in ${diff} Tagen — zwischen ${VERJAEHRUNG_ROT_TAGE} und ${VERJAEHRUNG_GELB_TAGE} Tagen (gelb). Bearbeitung priorisieren.`,
    };
  }
  return {
    status: 'gruen',
    wert: `${diff} Tage`,
    erklaerung: `Verjährung in ${diff} Tagen — noch genügend Zeit (grün).`,
  };
}

export function pruefePlausibilitaet(
  input: PlausibilitaetsInput,
  heute: Date = new Date(),
): PlausibilitaetsErgebnis {
  return {
    schadentag: pruefeSchadentag(input),
    abrechnung: pruefeAbrechnung(input),
    mietdauer: pruefeMietdauer(input),
    verjaehrung: pruefeVerjaehrung(input, heute),
  };
}

export const AMPEL_FARBEN: Record<PruefStatus, string> = {
  gruen: 'bg-green-500',
  gelb: 'bg-yellow-400',
  rot: 'bg-red-500',
  unbekannt: 'bg-gray-300',
};

export const AMPEL_LABELS: Record<PruefStatus, string> = {
  gruen: 'Grün',
  gelb: 'Gelb',
  rot: 'Rot',
  unbekannt: 'Unbekannt',
};
